import type { Node } from 'prosemirror-model'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'

import {
  kActiveCitationHighlightClass,
  kActiveCitationHighlightFill,
  kActiveCitationHighlightLeadingEdge,
  kActiveCitationHighlightTrailingEdge,
} from '../utils/artifact-citation-helpers'

export const activeCitationHighlightKey = new PluginKey<DecorationSet>('activeCitationHighlight')

type ActiveCitationHighlightPluginOptions = {
  /** The citation whose source is open beside the document — read per rebuild, null when none is. */
  getActiveCitationId: () => string | null
}

/**
 * Decoration marking the document's citations of the source open beside it, so a reader can see
 * which citation they are reading. Both citation forms are covered: the numbered reference marker
 * and the legal citation's authority text.
 *
 * Decorations are view-layer only: they can never serialize into the document, saved markdown,
 * or exports.
 */
export function createActiveCitationHighlightPlugin(options: ActiveCitationHighlightPluginOptions): Plugin<DecorationSet> {
  const build = (doc: Node) => buildDecorationSet(doc, options.getActiveCitationId())

  return new Plugin<DecorationSet>({
    key: activeCitationHighlightKey,
    state: {
      init: (_, state) => build(state.doc),
      apply: (transaction, decorationSet) =>
        transaction.docChanged || transaction.getMeta(activeCitationHighlightKey)
          ? build(transaction.doc)
          : decorationSet.map(transaction.mapping, transaction.doc),
    },
    props: {
      decorations(state) {
        return activeCitationHighlightKey.getState(state)
      },
    },
  })
}

function buildDecorationSet(doc: Node, activeCitationId: string | null): DecorationSet {
  if (!activeCitationId) return DecorationSet.empty

  const decorations: Decoration[] = []
  // A legal citation's authority text is one text node per run of identical marks, so an
  // italicised case name splits it ("*Loper Bright*, No. 22-451"). ProseMirror renders a span per
  // text node whatever the decoration spans, so the run is gathered and its pieces are given
  // rounding and padding only where the whole run starts and ends. Decorating each piece as a
  // complete pill instead draws them butted together, dipping at every seam.
  let authorityText: { from: number; to: number }[] = []

  const flushAuthorityText = () => {
    authorityText.forEach((piece, index) => {
      const className = [
        kActiveCitationHighlightFill,
        index === 0 ? kActiveCitationHighlightLeadingEdge : null,
        index === authorityText.length - 1 ? kActiveCitationHighlightTrailingEdge : null,
      ]
        .filter(Boolean)
        .join(' ')
      decorations.push(Decoration.inline(piece.from, piece.to, { class: className }))
    })
    authorityText = []
  }

  doc.descendants((node, position) => {
    if (node.type.name === 'referenceMarker') {
      if (node.attrs.citationId === activeCitationId) {
        decorations.push(Decoration.node(position, position + node.nodeSize, {}, { class: kActiveCitationHighlightClass }))
      }
      return false
    }

    if (!node.isText) return true

    const isActiveLegalCitation = node.marks.some((mark) => mark.type.name === 'legalCitation' && mark.attrs.citationId === activeCitationId)
    if (!isActiveLegalCitation) {
      flushAuthorityText()
      return true
    }

    if (authorityText.at(-1)?.to !== position) flushAuthorityText()
    authorityText.push({ from: position, to: position + node.nodeSize })
    return true
  })
  flushAuthorityText()

  return DecorationSet.create(doc, decorations)
}
